import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Grid, Stack, Card, Chip, Skeleton, Alert, alpha, useTheme,
  Switch, Button, TextField, IconButton,
  Select, MenuItem, FormControl, InputLabel, Collapse, Divider,
  CircularProgress, Tooltip, TablePagination,
} from '@mui/material';
import TableCount from '../components/TableCount';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from '../components/common/modal';
import {
  Zap, Wallet, Megaphone, Send, Repeat, Wrench, Play, Settings as SettingsIcon,
  ChevronDown, ChevronUp, CheckCircle2, Clock, Mail, ClipboardList,
  History, ShieldCheck, AlertTriangle,
} from 'lucide-react';
import axios from '../utils/axios';
import DashboardSectionHeader from '../components/DashboardSectionHeader';
import { notify } from '../utils/notificationService';

const CATEGORY_META = {
  'Revenue': { color: '#FE5F01', icon: Wallet },
  'Marketing & Growth': { color: '#A855F7', icon: Megaphone },
  'Leads & Matchmaking': { color: '#1890FF', icon: Send },
  'Retention': { color: '#f59e0b', icon: Repeat },
  'Operations': { color: '#64748b', icon: Wrench },
};
const CATEGORY_ORDER = ['Revenue', 'Marketing & Growth', 'Leads & Matchmaking', 'Retention', 'Operations'];

const CHANNEL_META = {
  email: { label: 'Email', icon: Mail },
};
const SCHEDULE_LABEL = { manual: 'Manual', hourly: 'Hourly', daily: 'Daily', weekly: 'Weekly' };

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const ConfigDialog = ({ open, onClose, automation, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && automation) {
      setForm({
        enabled: automation.config.enabled,
        schedule: automation.config.schedule,
        params: { ...automation.config.params },
      });
      setError('');
    }
  }, [open, automation]);

  if (!automation) return null;

  const setParam = (name, value) => setForm((f) => ({ ...f, params: { ...f.params, [name]: value } }));

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`/admin/automation-center/config/${automation.key}`, form);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminDialog open={open} onClose={!saving ? onClose : undefined} maxWidth="sm">
      <AdminDialogHeader
        title={automation.title}
        subtitle={automation.description}
        icon={Zap}
        onClose={!saving ? onClose : undefined}
      />
      <AdminDialogContent>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Enabled</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Include in scheduled runs</Typography>
          </Box>
          <Switch checked={!!form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} color="primary" />
        </Stack>

        <Grid container spacing={2} sx={{ mb: form.params && Object.keys(form.params || {}).length ? 1 : 0 }}>
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Schedule</InputLabel>
              <Select label="Schedule" value={form.schedule || 'manual'} onChange={(e) => setForm((f) => ({ ...f, schedule: e.target.value }))}>
                {['manual', 'hourly', 'daily', 'weekly'].map((s) => (
                  <MenuItem key={s} value={s}>{SCHEDULE_LABEL[s]}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {(automation.paramsMeta || []).map((p) => (
          <TextField
            key={p.name}
            fullWidth
            size="small"
            type="number"
            label={p.label}
            value={form.params?.[p.name] ?? ''}
            onChange={(e) => setParam(p.name, e.target.value)}
            sx={{ mt: 2 }}
          />
        ))}
      </AdminDialogContent>
      <AdminDialogActions>
        <Button onClick={onClose} color="inherit" disabled={saving} sx={{ fontWeight: 600 }}>Cancel</Button>
        <Button
          onClick={save}
          variant="contained"
          color="primary"
          disabled={saving}
          startIcon={saving && <CircularProgress size={16} color="inherit" />}
          sx={{ px: 3, borderRadius: 2, fontWeight: 700 }}
        >
          {saving ? 'Saving...' : 'Save Rule'}
        </Button>
      </AdminDialogActions>
    </AdminDialog>
  );
};

const AutomationCard = ({ automation, onToggle, onConfigure, onRun, running }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const meta = CATEGORY_META[automation.category] || CATEGORY_META.Operations;
  const accent = meta.color;
  const count = automation.evaluation?.count ?? 0;
  const hasCandidates = count > 0;
  const ChannelIcon = CHANNEL_META[automation.config.channel]?.icon || ClipboardList;
  const enabled = automation.config.enabled;

  return (
    <Card sx={{ p: { xs: 2, sm: 2.25 }, borderRadius: 3, height: '100%', minHeight: { xs: 'auto', sm: 240 }, display: 'flex', flexDirection: 'column', border: enabled ? `1px solid ${alpha(accent, 0.4)}` : undefined }}>
      <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 1 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, flexShrink: 0, bgcolor: alpha(accent, 0.14), color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <meta.icon size={20} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 14.5, lineHeight: 1.25 }}>{automation.title}</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25, lineHeight: 1.45 }}>{automation.description}</Typography>
        </Box>
        <Tooltip title={enabled ? 'Enabled' : 'Disabled'}>
          <Switch size="small" checked={enabled} onChange={(e) => onToggle(automation, e.target.checked)} />
        </Tooltip>
      </Stack>

      {/* Live candidate count */}
      {automation.error ? (
        <Alert severity="warning" sx={{ py: 0, my: 1, fontSize: 12 }}>Couldn’t evaluate right now.</Alert>
      ) : (
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ my: 0.75 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', lineHeight: 1, color: hasCandidates ? accent : 'text.disabled' }}>
            {count}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 600 }}>{automation.evaluation?.unit || 'candidates'}</Typography>
        </Stack>
      )}

      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Chip size="small" icon={<ChannelIcon size={12} />} label={CHANNEL_META[automation.config.channel]?.label || automation.config.channel}
          sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: alpha(accent, 0.10), color: accent, '& .MuiChip-icon': { color: accent } }} />
        <Chip size="small" icon={<Clock size={12} />} label={SCHEDULE_LABEL[automation.config.schedule]}
          sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: alpha(theme.palette.grey[500], 0.12), color: 'text.secondary', '& .MuiChip-icon': { color: 'text.disabled' } }} />
      </Stack>

      {/* Sample preview */}
      {automation.evaluation?.sample?.length > 0 && (
        <>
          <Button onClick={() => setOpen((o) => !o)} size="small" endIcon={open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            sx={{ alignSelf: 'flex-start', color: 'text.secondary', fontWeight: 700, fontSize: 12, px: 0.5, mb: 0.5 }}>
            {open ? 'Hide' : 'Preview audience'}
          </Button>
          <Collapse in={open}>
            <Stack spacing={0.75} sx={{ mb: 1, p: 1, borderRadius: 1.5, bgcolor: alpha(theme.palette.grey[500], 0.06) }}>
              {automation.evaluation.sample.map((s, i) => (
                <Box key={i}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.3 }} noWrap>{s.label}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }} noWrap>{s.sub}</Typography>
                </Box>
              ))}
            </Stack>
          </Collapse>
        </>
      )}

      <Box sx={{ flex: 1, minHeight: 8 }} />
      <Divider sx={{ my: 1 }} />
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Typography sx={{ fontSize: 11, color: 'text.disabled', fontWeight: 600 }}>
          {automation.config.last_run_at ? `Last run ${fmtDateTime(automation.config.last_run_at)}` : 'Never run'}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Configure">
            <IconButton aria-label="Configure automation" size="small" onClick={() => onConfigure(automation)} sx={{ color: 'text.secondary' }}>
              <SettingsIcon size={16} />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="contained"
            disabled={running}
            onClick={() => onRun(automation)}
            startIcon={running ? <CircularProgress size={13} color="inherit" /> : <Play size={14} />}
            sx={{ bgcolor: accent, '&:hover': { bgcolor: accent, filter: 'brightness(0.92)' }, px: 1.5, fontSize: 12.5 }}
          >
            {running ? 'Running...' : 'Run'}
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
};

const STATUS_COLOR = { success: '#16a34a', empty: '#94a3b8', error: '#dc2626' };

const AutomationCenterView = () => {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [configFor, setConfigFor] = useState(null);
  const [runningKey, setRunningKey] = useState(null);
  const [runningDue, setRunningDue] = useState(false);

  // Run history is paginated server-side (page is 1-based on the API).
  const loadLogs = useCallback(async (signal) => {
    try {
      const res = await axios.get('/admin/automation-center/logs', {
        params: { page: logsPage + 1, limit: logsRowsPerPage },
        signal,
      });
      setLogs(res.data.logs || []);
      setLogsTotal(res.data.total || 0);
    } catch (err) { /* non-fatal */ }
  }, [logsPage, logsRowsPerPage]);

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/admin/automation-center/catalog', { signal });
      setData(res.data);
    } catch (err) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
      setError(err.response?.data?.msg || 'Failed to load automations');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);
  // Reload the history whenever the page / page-size changes (and on mount).
  useEffect(() => {
    const controller = new AbortController();
    loadLogs(controller.signal);
    return () => controller.abort();
  }, [loadLogs]);

  const handleLogsPageChange = (_e, newPage) => setLogsPage(newPage);
  const handleLogsRowsPerPageChange = (e) => {
    setLogsRowsPerPage(parseInt(e.target.value, 10));
    setLogsPage(0);
  };

  const toggle = async (automation, enabled) => {
    // optimistic
    setData((d) => ({ ...d, automations: d.automations.map((a) => a.key === automation.key ? { ...a, config: { ...a.config, enabled } } : a) }));
    try {
      await axios.put(`/admin/automation-center/config/${automation.key}`, { enabled }, { skipNotify: 'success' });
      notify({ message: `${automation.title} ${enabled ? 'enabled' : 'disabled'}`, severity: 'success' });
    } catch (err) {
      notify({ message: `Couldn’t update ${automation.title}`, severity: 'error', system: true, url: '/automation-center' });
      load();
    }
  };

  const run = async (automation) => {
    setRunningKey(automation.key);
    try {
      const res = await axios.post(`/admin/automation-center/run/${automation.key}`, null, { skipNotify: 'success' });
      const log = res.data.log;
      const dispatched = log.mode === 'simulated' ? '(simulated)' : 'dispatched';
      notify({
        title: 'Automation complete',
        message: `${log.title}: ${log.candidate_count} ${log.candidate_count === 1 ? 'candidate' : 'candidates'} ${dispatched}`,
        severity: log.candidate_count > 0 ? 'success' : 'info',
        system: true,
        url: '/automation-center',
      });
      // Jump to the first page so the just-created run is visible, then refresh.
      if (logsPage !== 0) setLogsPage(0);
      else await loadLogs();
      setData((d) => ({ ...d, automations: d.automations.map((a) => a.key === automation.key ? { ...a, config: { ...a.config, last_run_at: log.run_at, last_count: log.candidate_count } } : a) }));
    } catch (err) {
      notify({
        title: 'Automation failed',
        message: `${automation.title}: ${err.response?.data?.msg || 'Run failed'}`,
        severity: 'error',
        system: true,
        url: '/automation-center',
      });
    } finally {
      setRunningKey(null);
    }
  };

  const runDue = async () => {
    setRunningDue(true);
    try {
      const res = await axios.post('/admin/automation-center/run-due', null, { skipNotify: 'success' });
      notify({
        title: 'Automations run',
        message: `Ran ${res.data.ranCount} due automation(s)`,
        severity: 'success',
        system: true,
        url: '/automation-center',
      });
      await load();
      if (logsPage !== 0) setLogsPage(0);
      else await loadLogs();
    } catch (err) {
      notify({
        title: 'Automations failed',
        message: 'Failed to run due automations',
        severity: 'error',
        system: true,
        url: '/automation-center',
      });
    } finally {
      setRunningDue(false);
    }
  };

  const automations = data?.automations || [];
  const activeCount = automations.filter((a) => a.config.enabled).length;
  const totalCandidates = automations.reduce((s, a) => s + (a.evaluation?.count || 0), 0);

  const summary = [
    { label: 'Automations', value: automations.length, color: theme.palette.primary.main, icon: <Zap size={18} /> },
    { label: 'Active', value: activeCount, color: '#16a34a', icon: <CheckCircle2 size={18} /> },
    { label: 'Candidates', value: totalCandidates, color: '#f59e0b', icon: <ClipboardList size={18} /> },
    { label: 'Recent Runs', value: logs.length, color: '#A855F7', icon: <History size={18} /> },
  ];

  const grouped = CATEGORY_ORDER
    .map((cat) => ({ cat, items: automations.filter((a) => a.category === cat) }))
    .filter((g) => g.items.length > 0);

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', width: '100%' }}>
      <DashboardSectionHeader
        title="Automation Center"
        subtitle="Data-driven revenue, marketing, lead, retention & ops automations — configure, preview the audience and run."
        onRefresh={() => { load(); loadLogs(); }}
        refreshTooltip="Refresh"
        action={
          <Button variant="contained" size="small" disabled={runningDue} onClick={runDue}
            startIcon={runningDue ? <CircularProgress size={14} color="inherit" /> : <Zap size={16} />}
            sx={{ fontWeight: 700 }}>
            Run due
          </Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError('')}>{error}</Alert>}

      {!loading && data && (
        <Alert
          icon={data.liveSend ? <ShieldCheck size={18} /> : <AlertTriangle size={18} />}
          severity={data.liveSend ? 'success' : 'info'}
          sx={{ mb: 2.5, borderRadius: 2.5, fontWeight: 500 }}
        >
          {data.liveSend
            ? 'Live mode is ON — enabled automations will send real emails to customers.'
            : 'Simulation mode — automations evaluate live data and log results, but no emails are sent. Set AUTOMATION_LIVE_SEND=on on the server to go live.'}
        </Alert>
      )}

      {/* Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {(loading ? Array.from({ length: 4 }) : summary).map((s, i) => (
          <Grid size={{ xs: 6, md: 3 }} key={i} sx={{ display: 'flex', '& > *': { flex: 1 } }}>
            {loading ? (
              <Skeleton variant="rounded" height={80} sx={{ borderRadius: 3 }} />
            ) : (
              <Card sx={{ p: { xs: 1.75, sm: 2 }, borderRadius: 3, display: 'flex', alignItems: 'center', height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha(s.color, 0.14), color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{s.icon}</Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography noWrap sx={{ fontSize: { xs: 10, sm: 11 }, fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</Typography>
                    <Typography sx={{ fontSize: { xs: '1.25rem', sm: '1.4rem' }, fontWeight: 800, lineHeight: 1.1 }}>{Number(s.value).toLocaleString('en-IN')}</Typography>
                  </Box>
                </Stack>
              </Card>
            )}
          </Grid>
        ))}
      </Grid>

      {/* Automations grouped by category */}
      {loading ? (
        <Grid container spacing={2.5}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}><Skeleton variant="rounded" height={260} sx={{ borderRadius: 3 }} /></Grid>
          ))}
        </Grid>
      ) : (
        grouped.map(({ cat, items }) => {
          const meta = CATEGORY_META[cat];
          return (
            <Box key={cat} sx={{ mb: 3.5 }}>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
                <Box sx={{ color: meta.color, display: 'flex' }}><meta.icon size={18} /></Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>{cat}</Typography>
                <Chip size="small" label={items.length} sx={{ height: 20, fontSize: 11, fontWeight: 800, bgcolor: alpha(meta.color, 0.12), color: meta.color }} />
              </Stack>
              <Grid container spacing={2.5}>
                {items.map((a) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={a.key} sx={{ display: 'flex', '& > *': { flex: 1 } }}>
                    <AutomationCard
                      automation={a}
                      running={runningKey === a.key}
                      onToggle={toggle}
                      onConfigure={setConfigFor}
                      onRun={run}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        })
      )}

      {/* Run history */}
      {!loading && (
        <Card sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, mb: 2 }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
            <Box sx={{ color: theme.palette.primary.main, display: 'flex' }}><History size={18} /></Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>Recent Run History</Typography>
            <TableCount total={logsTotal} label="runs" />
          </Stack>
          {logsTotal === 0 ? (
            <Typography sx={{ color: 'text.disabled', fontSize: 13, py: 3, textAlign: 'center' }}>No runs yet — hit “Run now” on any automation.</Typography>
          ) : (
            <>
            <Stack divider={<Divider />}>
              {logs.map((log) => (
                <Stack key={log._id} direction="row" alignItems="center" spacing={1.5} sx={{ py: 1.25 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_COLOR[log.status] || '#94a3b8', flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700 }} noWrap>{log.title}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }} noWrap>{fmtDateTime(log.run_at)} · {log.triggered_by}</Typography>
                  </Box>
                  <Chip size="small" label={`${log.candidate_count} found`} sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.10), color: theme.palette.primary.main }} />
                  <Chip size="small" label={log.mode === 'simulated' ? 'Simulated' : 'Live'}
                    sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: alpha(log.mode === 'live' ? theme.palette.success.main : theme.palette.grey[500], 0.14), color: log.mode === 'live' ? theme.palette.success.dark : 'text.secondary' }} />
                </Stack>
              ))}
            </Stack>
            <TablePagination
              component="div"
              count={logsTotal}
              page={logsPage}
              onPageChange={handleLogsPageChange}
              rowsPerPage={logsRowsPerPage}
              onRowsPerPageChange={handleLogsRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{ borderTop: `1px solid ${theme.palette.divider}`, mt: 1 }}
            />
            </>
          )}
        </Card>
      )}

      <ConfigDialog open={!!configFor} automation={configFor} onClose={() => setConfigFor(null)} onSaved={load} />
    </Box>
  );
};

export default AutomationCenterView;
